import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';

interface State<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /**
   * The failed request itself, when there was one — additive alongside
   * `error`'s already-formatted message. A caller that needs to branch on a
   * coded refusal (`{ code: 'SOME_CODE', message }`, e.g. a 404 that means
   * "not configured yet" rather than "broken") reads `error.response.data.code`
   * off this rather than parsing the string `error` back apart.
   */
  rawError: unknown;
}

/**
 * Fetch helper for list/detail screens. Paginated endpoints answer
 * `{ data, meta }` while single-purpose ones answer a bare value, so unwrap
 * both shapes here rather than in every screen.
 */
export function useApi<T>(url: string | null, params?: Record<string, unknown>) {
  const [state, setState] = useState<State<T>>({
    data: null,
    loading: !!url,
    error: null,
    rawError: null,
  });
  const key = JSON.stringify(params ?? {});

  const load = useCallback(() => {
    if (!url) return;
    setState((s) => ({ ...s, loading: true, error: null, rawError: null }));
    api
      .get(url, { params: params ?? undefined })
      .then(({ data }) => setState({ data: data as T, loading: false, error: null, rawError: null }))
      .catch((e) =>
        setState({
          data: null,
          loading: false,
          error: e?.response?.data?.message ?? 'Could not load this data',
          rawError: e,
        }),
      );
    // params is compared by value through `key`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, key]);

  useEffect(load, [load]);

  return { ...state, reload: load };
}

/** Unwraps a paginated `{ data, meta }` envelope into rows plus total. */
export function rowsOf<T>(payload: unknown): { rows: T[]; total: number } {
  if (Array.isArray(payload)) return { rows: payload as T[], total: payload.length };
  const p = payload as { data?: T[]; meta?: { total?: number } } | null;
  return { rows: p?.data ?? [], total: p?.meta?.total ?? p?.data?.length ?? 0 };
}
